/*
 * Clientes cadastrados pelo portal.
 *
 * A busca aceita nome ou telefone em qualquer formato — quem atende recebe o
 * numero por telefone ou WhatsApp e digita como veio, sem traduzir para o
 * formato do banco.
 */
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { CustomerDto, MikrotikDto } from '../lib/types';
import { CustomerPanel } from './CustomerPanel';
import { Alert, Button, Card, Spinner } from '../components/ui';

type Filtro = 'todos' | 'bloqueados' | 'ativos';

const FILTROS: { chave: Filtro; rotulo: string }[] = [
  { chave: 'todos', rotulo: 'Todos' },
  { chave: 'ativos', rotulo: 'Ativos' },
  { chave: 'bloqueados', rotulo: 'Bloqueados' },
];

export function dataCurta(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CustomersPage() {
  const { can } = useAuth();
  const podeExportar = can(PERMISSIONS.CUSTOMER_EXPORT);

  const [clientes, setClientes] = useState<CustomerDto[] | null>(null);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  const [vlan, setVlan] = useState<string | null>(null);
  const [vlans, setVlans] = useState<string[]>([]);
  const [equipamentos, setEquipamentos] = useState<MikrotikDto[]>([]);
  const [selecionado, setSelecionado] = useState<number | null>(null);
  /** Incrementado para repetir a mesma busca depois de bloquear ou anonimizar. */
  const [versao, setVersao] = useState(0);

  const consulta = montarConsulta(buscaAplicada, filtro, mikrotikId, vlan);

  useEffect(() => {
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => setEquipamentos(data))
      // Sem a lista, o seletor simplesmente nao aparece: a busca continua.
      .catch(() => setEquipamentos([]));
  }, []);

  useEffect(() => {
    setVlan(null);
    if (mikrotikId === null) {
      setVlans([]);
      return;
    }
    apiRequest<string[]>(`/admin/customers/vlans?mikrotikId=${mikrotikId}`)
      .then((lista) => setVlans(lista))
      .catch(() => setVlans([]));
  }, [mikrotikId]);

  useEffect(() => {
    let ativo = true;
    setClientes(null);
    setErro(null);

    apiList<CustomerDto>(`/admin/customers${consulta}`)
      .then(({ data, meta }) => {
        if (!ativo) return;
        setClientes(data);
        setTotal(meta?.total ?? data.length);
      })
      .catch((caught: unknown) => {
        if (!ativo) return;
        setClientes([]);
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      });

    return () => {
      ativo = false;
    };
  }, [consulta, versao]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-sm text-slate-500">
            Quem se cadastrou pelo portal para acessar o Wi-Fi.
          </p>
        </div>

        {podeExportar ? (
          /* Link comum, e nao fetch: o navegador cuida do download e do nome
             do arquivo, e o cookie de sessao vai junto. */
          <a
            href={`/api/admin/customers/export.csv${montarConsulta(buscaAplicada, filtro, mikrotikId, vlan, false)}`}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Exportar CSV
          </a>
        ) : null}
      </header>

      <Card>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            setBuscaAplicada(busca.trim());
          }}
        >
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Nome ou telefone"
            className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
          <Button type="submit">Buscar</Button>

          {buscaAplicada ? (
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setBusca('');
                setBuscaAplicada('');
              }}
            >
              Limpar
            </Button>
          ) : null}

          <span className="mx-1 h-6 w-px bg-slate-200" />

          {FILTROS.map((item) => (
            <Button
              key={item.chave}
              type="button"
              variant={filtro === item.chave ? 'primary' : 'ghost'}
              onClick={() => setFiltro(item.chave)}
            >
              {item.rotulo}
            </Button>
          ))}

          {/*
            Aparece com QUALQUER equipamento cadastrado, inclusive um so. Nao e
            redundante: filtrar exclui quem se cadastrou e nunca chegou a abrir
            sessao. E, mesmo que fosse, esconder um recurso que o operador sabe
            que existe custa mais do que a linha a mais na barra.
          */}
          {equipamentos.length > 0 ? (
            <>
              <span className="mx-1 h-6 w-px bg-slate-200" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Equipamento
                <select
                  value={mikrotikId ?? ''}
                  onChange={(evento) =>
                    setMikrotikId(evento.target.value ? Number(evento.target.value) : null)
                  }
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                >
                  <option value="">Todos</option>
                  {equipamentos.map((equipamento) => (
                    <option key={equipamento.id} value={equipamento.id}>
                      {equipamento.name}
                    </option>
                  ))}
                </select>
              </label>

              {vlans.length > 0 ? (
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  VLAN
                  <select
                    value={vlan ?? ''}
                    onChange={(evento) => setVlan(evento.target.value || null)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  >
                    <option value="">Todas</option>
                    {vlans.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
        </form>

        {mikrotikId !== null ? (
          <p className="mt-3 text-xs text-slate-500">
            Mostrando quem <strong>já acessou pelo menos uma vez</strong> por este equipamento
            {vlan ? (
              <>
                {' '}
                na VLAN <strong>{vlan}</strong>
              </>
            ) : null}
            {' '}— não apenas o último acesso. A mesma pessoa aparece em todos os pontos por onde passou.
          </p>
        ) : null}
      </Card>

      {erro ? <Alert>{erro}</Alert> : null}

      <Card
        title={clientes ? `${total} ${total === 1 ? 'cliente' : 'clientes'}` : 'Clientes'}
      >
        {!clientes ? (
          <Spinner />
        ) : clientes.length === 0 ? (
          <p className="text-sm text-slate-500">
            {buscaAplicada
              ? 'Nenhum cliente encontrado para esta busca.'
              : 'Nenhum cliente ainda. Eles aparecem aqui conforme se cadastram no portal.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-3">Nome</th>
                  <th className="py-2 pr-3">Telefone</th>
                  <th className="py-2 pr-3 text-right">Acessos</th>
                  <th className="py-2 pr-3">Último acesso</th>
                  <th className="py-2 pr-3">Equipamento</th>
                  <th className="py-2 pr-3">VLAN</th>
                  <th className="py-2 pr-3">Situação</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente) => (
                  <tr
                    key={cliente.id}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors ${
                      selecionado === cliente.id ? 'bg-brand-50/50' : ''
                    }`}
                  >
                    <td className="py-2.5 pr-3 font-medium text-slate-800">
                      {cliente.name ?? <span className="text-slate-400">— anonimizado —</span>}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">
                      {cliente.phone ? formatarTelefone(cliente.phone) : '—'}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                      {cliente.accessCount}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {dataCurta(cliente.lastSeenAt)}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {cliente.lastMikrotikName ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">
                      {cliente.lastVlan ?? <span className="font-sans text-slate-400">—</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Situacao cliente={cliente} />
                    </td>
                    <td className="py-2.5 text-right">
                      <Button variant="ghost" onClick={() => setSelecionado(cliente.id)}>
                        Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selecionado !== null ? (
        <CustomerPanel
          customerId={selecionado}
          onClose={() => setSelecionado(null)}
          onMudou={() => setVersao((atual) => atual + 1)}
        />
      ) : null}
    </div>
  );
}

function Situacao({ cliente }: { cliente: CustomerDto }) {
  if (cliente.isAnonymized) {
    return <Etiqueta tom="slate">Anonimizado</Etiqueta>;
  }
  if (cliente.isBlocked) {
    return <Etiqueta tom="red">Bloqueado</Etiqueta>;
  }
  if (cliente.consentMarketing) {
    return <Etiqueta tom="emerald">Aceita ofertas</Etiqueta>;
  }
  return <span className="text-xs text-slate-400">—</span>;
}

export function Etiqueta({
  tom,
  children,
}: {
  tom: 'slate' | 'red' | 'emerald' | 'brand';
  children: React.ReactNode;
}) {
  const tons = {
    slate: 'bg-slate-100 text-slate-600',
    red: 'bg-red-100 text-red-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    brand: 'bg-brand-50 text-brand-800',
  } as const;

  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tons[tom]}`}>{children}</span>
  );
}

/** (11) 98765-4321 a partir do E.164 guardado. */
export function formatarTelefone(e164: string): string {
  const digitos = e164.replace(/\D+/g, '').replace(/^55/, '');
  if (digitos.length < 10) return e164;

  const ddd = digitos.slice(0, 2);
  const numero = digitos.slice(2);
  const corte = numero.length === 9 ? 5 : 4;
  return `(${ddd}) ${numero.slice(0, corte)}-${numero.slice(corte)}`;
}

function montarConsulta(
  busca: string,
  filtro: Filtro,
  mikrotikId: number | null,
  vlan: string | null,
  comPaginacao = true,
): string {
  const partes: string[] = [];

  if (busca) partes.push(`busca=${encodeURIComponent(busca)}`);
  if (filtro === 'bloqueados') partes.push('blocked=true');
  if (filtro === 'ativos') partes.push('blocked=false');
  if (mikrotikId !== null) partes.push(`mikrotikId=${mikrotikId}`);
  if (vlan) partes.push(`vlan=${encodeURIComponent(vlan)}`);
  if (comPaginacao) partes.push('perPage=100');

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}
